import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { sectionToHtml, END_CLASSES } from "@/utils/sectionMarkup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function Version({ title, s, tone }) {
  return (
    <div className={`p-3 rounded border text-xs ${tone}`}>
      <div className="font-semibold mb-1">{title}</div>
      <div className="font-mono text-[#64748B] mb-2">{s.page_path} · {s.placement === "end" ? "end of page" : `${s.placement} "${(s.anchor_text || "").slice(0, 60)}"`}</div>
      <div className="bg-white rounded p-3" dangerouslySetInnerHTML={{ __html: sectionToHtml(s, END_CLASSES) }} />
    </div>
  );
}

function SectionCard({ s, onDecide, onRemove }) {
  const [notes, setNotes] = useState("");
  const pending = s.status === "pending" || !!s.pending_update;
  return (
    <div className="border border-[#E2E8F0] rounded p-4" data-testid={`admin-section-${s.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-xs text-[#64748B]">From <strong>{s.editor_name}</strong> ({s.editor_email}) · {new Date(s.updated_at).toLocaleString()}</div>
        <Badge className={s.status === "approved" ? "bg-green-100 text-green-800 hover:bg-green-100" : s.status === "rejected" ? "bg-red-100 text-red-800 hover:bg-red-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}>
          {s.pending_update ? "edit pending" : s.status}
        </Badge>
      </div>
      <div className={`grid gap-3 ${s.pending_update ? "md:grid-cols-2" : ""}`}>
        <Version title={s.pending_update ? "Live now" : "Section"} s={s} tone="bg-slate-50 border-slate-200" />
        {s.pending_update && <Version title="Proposed edit" s={s.pending_update} tone="bg-green-50 border-green-200" />}
      </div>
      {s.reason && <div className="text-xs mt-3 p-2 bg-[#F8FAFC] rounded"><strong>SEO reasoning:</strong> {s.reason}</div>}
      {pending ? (
        <div className="mt-3 space-y-2">
          <textarea className="w-full text-xs border border-[#E2E8F0] rounded p-2" rows={2} placeholder="Optional notes for Approve, REQUIRED for Reject"
            value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`admin-section-notes-${s.id}`} />
          <div className="flex gap-2 justify-end">
            <Button size="sm" onClick={() => onDecide(s, "approve", notes)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`admin-section-approve-${s.id}`}>Approve</Button>
            <Button size="sm" variant="outline" onClick={() => onDecide(s, "reject", notes)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`admin-section-reject-${s.id}`}>Reject</Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end mt-3">
          <Button size="sm" variant="outline" onClick={() => onRemove(s)} className="text-red-600 border-red-300 hover:bg-red-50" data-testid={`admin-section-remove-${s.id}`}>Remove from site</Button>
        </div>
      )}
    </div>
  );
}

export default function AdminSectionsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");

  const refresh = () => axios.get(`${API}/admin/seo/sections`, { headers: authHeaders(), params: filter === "all" ? {} : { status: filter } })
    .then((r) => setItems(r.data || [])).catch(() => toast.error("Failed to load sections"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [filter]);

  const decide = async (s, decision, notes) => {
    if (decision === "reject" && !notes.trim()) { toast.error("Please add notes explaining why before rejecting"); return; }
    try {
      await axios.post(`${API}/admin/seo/sections/${s.id}/decide`, { decision, notes }, { headers: authHeaders() });
      toast.success(decision === "approve" ? "Approved — section is live." : "Rejected.");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Decision failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.heading}" from ${s.page_path}?`)) return;
    try {
      await axios.delete(`${API}/admin/seo/sections/${s.id}`, { headers: authHeaders() });
      toast.success("Section removed.");
      refresh();
    } catch (e) { toast.error("Remove failed"); }
  };

  return (
    <Card data-testid="admin-sections-tab">
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>New Sections from SEO Agency</span>
          <div className="flex gap-1">
            {["pending", "approved", "rejected", "all"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`admin-section-filter-${f}`}
                className={`text-xs px-3 py-1 rounded ${filter === f ? "bg-[#F97316] text-white" : "bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]"}`}>{f}</button>
            ))}
          </div>
        </CardTitle>
        <p className="text-xs text-[#64748B] mt-1">New headline + paragraph sections. Sections on the 5 gated pages wait here for your approval; all other pages go live instantly and can be removed here anytime.</p>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? <p className="text-sm text-[#94A3B8] py-6 text-center">{filter === "pending" ? "No pending sections. You're all caught up." : `No ${filter} sections.`}</p>
          : <div className="space-y-4">{items.map((s) => <SectionCard key={s.id} s={s} onDecide={decide} onRemove={remove} />)}</div>}
      </CardContent>
    </Card>
  );
}
